import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Row, Col, DatePicker, Select, Button, Empty, Spin, Popconfirm, message } from 'antd';
import { SearchOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EChartsOption } from 'echarts';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import { usePrivateData } from '@/api/private';
import { format2 } from '@/utils/charts';
import { useStations } from '@/hooks/useStations';
import { unbindStation } from '@/api/stations';
import type { AGCRow, FDKHRow, PredictionRow, PredictionSummaryRow } from '@/types/api';

const TIME_STEP_HOURS = 5.0 / 60.0; // 1/12 小时

const COLORS = {
  agcTarget: '#faad14',
  agcActual: '#f5222d',
  pvAvail: '#13c2c2',
  pvActual: '#0d9488',
  battery: '#722ed1',
  penalty: '#ff4d4f',
  ok: '#52c41a',
  daPred: '#9ca3af',
  ustPred: '#13c2c2',
  ustRawPred: '#2563eb',
};

/** ECharts 统一基线配置 */
function baseChartOption(): EChartsOption {
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      valueFormatter: (value: any) => (typeof value === 'number' ? format2(value) : value),
    },
    toolbox: { show: false },
    dataZoom: [{ type: 'slider', bottom: 10 }],
    grid: { left: 60, right: 20, top: 40, bottom: 115 }
  };
}

/** 格式化数字为 MWh */
function fmtMWh(v: number): string {
  return `${format2(v)} MWh`;
}

/** 扁平指标 */
const Metric: React.FC<{ title: string; value: string; delta?: string; deltaColor?: 'red' | 'green' | 'off' }> = ({
  title,
  value,
  delta,
  deltaColor = 'off',
}) => {
  const colorMap = { red: '#ef4444', green: '#22c55e', off: '#9ca3af' };
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{value}</div>
      {delta && (
        <div style={{ fontSize: '0.75rem', color: colorMap[deltaColor], marginTop: 2 }}>{delta}</div>
      )}
    </div>
  );
};

const PrivatePage: React.FC = () => {
  const yesterday = dayjs().subtract(1, 'day');

  const { data: stationsData, isLoading: stationsLoading } = useStations();
  const stations = stationsData?.stations ?? [];

  const [selectedStation, setSelectedStation] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(yesterday);
  const [queryParams, setQueryParams] = useState<{ station: string; date: string }>({
    station: '',
    date: yesterday.format('YYYY-MM-DD'),
  });

  useEffect(() => {
    if (stations.length > 0 && !selectedStation) {
      setSelectedStation(stations[0]);
      setQueryParams({ station: stations[0], date: yesterday.format('YYYY-MM-DD') });
    }
  }, [stations, selectedStation, yesterday]);

  const { data, isLoading, error, refetch } = usePrivateData(queryParams);

  const queryClient = useQueryClient();
  const unbindMut = useMutation({
    mutationFn: (station: string) => unbindStation(station),
    onSuccess: () => {
      message.success('场站已解绑');
      queryClient.invalidateQueries({ queryKey: ['stations'] });
      setSelectedStation('');
      setQueryParams({ station: '', date: yesterday.format('YYYY-MM-DD') });
    },
    onError: (e: unknown) => {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message || '解绑失败');
    },
  });

  const agc: AGCRow[] = data?.data.agc ?? [];
  const fdkh: FDKHRow[] = data?.data.fdkh ?? [];
  const predictions: PredictionRow[] = data?.data.predictions ?? [];
  const predictionSummary: PredictionSummaryRow[] = data?.data.prediction_summary ?? [];

  // ========== AGC 计算 ==========
  const agcMetrics = useMemo(() => {
    if (agc.length === 0) return null;
    const dt = TIME_STEP_HOURS;
    const targetMwh = agc.reduce((s, r) => s + r.agc_total_target_power, 0) * dt;
    const actualMwh = agc.reduce((s, r) => s + r.agc_total_actual_power, 0) * dt;
    const pvActualMwh = agc.reduce((s, r) => s + r.pv_actual_power, 0) * dt;
    const charge = agc
      .filter((r) => r.battery_actual_power < 0)
      .reduce((s, r) => s + r.battery_actual_power, 0) * dt;
    const discharge = agc
      .filter((r) => r.battery_actual_power > 0)
      .reduce((s, r) => s + r.battery_actual_power, 0) * dt;
    const socMwh = agc.map((_r, i) =>
      agc.slice(0, i + 1).reduce((s, rr) => s - rr.battery_actual_power * dt, 0)
    );
    const finalSoc = socMwh[socMwh.length - 1] ?? 0;
    return { targetMwh, actualMwh, pvActualMwh, charge, discharge, finalSoc, socMwh };
  }, [agc]);

  // ========== AGC 双轴图表 ==========
  const agcChartOption: EChartsOption = useMemo(() => {
    if (agc.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: [{ type: 'value' }, { type: 'value' }],
        series: [],
      };
    }
    const xData = agc.map((r) => r.record_time.slice(11, 16));
    const socMwh = agcMetrics?.socMwh ?? [];
    return {
      ...baseChartOption(),
      legend: {
        data: ['AGC目标功率', 'AGC实际功率', '光伏可用功率', '储能实际功率', '光伏实际功率', 'SOC净留存'],
        bottom: 55,
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: [
        {
          type: 'value',
          name: '功率 (MW)',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
          axisLabel: { color: '#9ca3af' },
        },
        {
          type: 'value',
          name: '电量 (MWh)',
          show: false,
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'AGC目标功率',
          type: 'line',
          data: agc.map((r) => r.agc_total_target_power),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.agcTarget, width: 2 },
          itemStyle: { color: COLORS.agcTarget },
        },
        {
          name: 'AGC实际功率',
          type: 'line',
          data: agc.map((r) => r.agc_total_actual_power),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.agcActual, width: 2 },
          itemStyle: { color: COLORS.agcActual },
        },
        {
          name: '光伏可用功率',
          type: 'line',
          data: agc.map((r) => r.pv_available_power),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.pvAvail, width: 2 },
          itemStyle: { color: COLORS.pvAvail },
        },
        {
          name: '储能实际功率',
          type: 'line',
          data: agc.map((r) => r.battery_actual_power),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.battery, width: 2 },
          itemStyle: { color: COLORS.battery },
        },
        {
          name: '光伏实际功率',
          type: 'line',
          data: agc.map((r) => r.pv_actual_power),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.pvActual, width: 2 },
          itemStyle: { color: COLORS.pvActual },
        },
        {
          name: 'SOC净留存',
          type: 'line',
          yAxisIndex: 1,
          data: socMwh,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.daPred, width: 2, type: 'dotted' },
          itemStyle: { color: COLORS.daPred },
        },
      ],
    };
  }, [agc, agcMetrics]);

  // ========== FDKH 计算 ==========
  const fdkhMetrics = useMemo(() => {
    if (fdkh.length === 0) return null;
    const planMwh = fdkh.reduce((s, r) => s + (r.fdkh_planned_energy ?? 0), 0);
    const actualMwh = fdkh.reduce((s, r) => s + (r.fdkh_actual_energy ?? 0), 0);
    const assessMwh = fdkh.reduce((s, r) => s + (r.fdkh_assessment_energy ?? 0), 0);

    const ratios = fdkh.map((r) => {
      const dev = r.fdkh_deviation_energy ?? 0;
      const assess = r.fdkh_assessment_energy ?? 0;
      if (dev === 0) return assess === 0 ? 0 : 1;
      return Math.abs(assess / dev);
    });
    const sumHigh = fdkh.reduce((s, r, i) => s + (ratios[i] > 1 ? (r.fdkh_assessment_energy ?? 0) : 0), 0);

    return { planMwh, actualMwh, assessMwh, sumHigh, ratios };
  }, [fdkh]);

  // ========== FDKH 线+柱图表 ==========
  const fdkhChartOption: EChartsOption = useMemo(() => {
    if (fdkh.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: [{ type: 'value' }, { type: 'value' }],
        series: [],
      };
    }
    const xData = fdkh.map((r) => r.record_time.slice(11, 16));
    const barColors = (fdkhMetrics?.ratios ?? []).map((ratio) =>
      ratio > 1 ? COLORS.penalty : COLORS.ok
    );
    return {
      ...baseChartOption(),
      legend: { data: ['计划电量', '实际上网电量', '考核电量'], bottom: 55 },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: [
        {
          type: 'value',
          name: '电量 (MWh)',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
          axisLabel: { color: '#9ca3af' },
        },
        {
          type: 'value',
          show: false,
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '计划电量',
          type: 'line',
          data: fdkh.map((r) => r.fdkh_planned_energy),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.battery, width: 2, type: 'dashed' },
          itemStyle: { color: COLORS.battery },
        },
        {
          name: '实际上网电量',
          type: 'line',
          data: fdkh.map((r) => r.fdkh_actual_energy),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: COLORS.agcActual, width: 2 },
          itemStyle: { color: COLORS.agcActual },
        },
        {
          name: '考核电量',
          type: 'bar',
          yAxisIndex: 1,
          data: fdkh.map((r) => r.fdkh_assessment_energy),
          itemStyle: {
            color: (params: any) => barColors[params.dataIndex] ?? COLORS.ok,
          },
        },
      ],
    };
  }, [fdkh, fdkhMetrics]);

  // ========== Prediction 图表 ==========
  const predictionChartOption: EChartsOption = useMemo(() => {
    if (agc.length === 0) {
      return {
        ...baseChartOption(),
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      };
    }
    const xData = agc.map((r) => r.record_time.slice(11, 16));
    const series: any[] = [
      {
        name: '实际光伏功率',
        type: 'line',
        data: agc.map((r) => r.pv_actual_power),
        smooth: true,
        symbol: 'none',
        lineStyle: { color: COLORS.pvActual, width: 2 },
        itemStyle: { color: COLORS.pvActual },
      },
    ];

    if (predictions.length > 0) {
      // Align prediction timestamps to AGC time axis
      const predMap = new Map<string, PredictionRow>();
      predictions.forEach((p) => {
        const key = p.record_time.slice(11, 16);
        predMap.set(key, p);
      });

      const ustData = xData.map((t) => predMap.get(t)?.ultra_short_term_pred ?? null);
      const ustRawData = xData.map((t) => predMap.get(t)?.ultra_short_term_raw_pred ?? null);
      const daData = xData.map((t) => predMap.get(t)?.dayahead_pred ?? null);

      series.push({
        name: '超短期预测',
        type: 'line',
        data: ustData,
        smooth: true,
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: COLORS.ustPred, width: 2, type: 'dashed' },
        itemStyle: { color: COLORS.ustPred },
      });
      if (ustRawData.some((v) => v !== null)) {
        series.push({
          name: '超短期原始值',
          type: 'line',
          data: ustRawData,
          smooth: true,
          symbol: 'none',
          connectNulls: false,
          lineStyle: { color: COLORS.ustRawPred, width: 2, type: 'dashed' },
          itemStyle: { color: COLORS.ustRawPred },
        });
      }
      series.push({
        name: '日前预测',
        type: 'line',
        data: daData,
        smooth: true,
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: COLORS.daPred, width: 2, type: 'dotted' },
        itemStyle: { color: COLORS.daPred },
      });
    }

    return {
      ...baseChartOption(),
      legend: { data: series.map((s) => s.name), bottom: 40 },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#9ca3af' },
      },
      yAxis: {
        type: 'value',
        name: '功率 (MW)',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af' },
      },
      series,
    };
  }, [agc, predictions]);

  // ========== 查询按钮 ==========
  const handleQuery = useCallback(() => {
    setQueryParams({
      station: selectedStation,
      date: selectedDate.format('YYYY-MM-DD'),
    });
  }, [selectedStation, selectedDate]);

  const handleStationChange = useCallback((value: string) => {
    setSelectedStation(value);
  }, []);

  const displayStation = selectedStation;

  if (stationsLoading) {
    return (
      <PageContainer title="场站日间运行数据复盘">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Spin size="large" tip="正在加载场站列表..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="场站日间运行数据复盘">
      {/* 控制栏 */}
      <div style={{ paddingBottom: 16, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Select
              value={displayStation || undefined}
              onChange={handleStationChange}
              options={stations.map((s) => ({ label: s, value: s }))}
              style={{ width: '100%' }}
              placeholder="选择目标场站"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <DatePicker
              value={selectedDate}
              onChange={(d) => d && setSelectedDate(d)}
              style={{ width: '100%' }}
              placeholder="选择业务日期"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} style={{ width: '100%' }}>
              查询
            </Button>
          </Col>
          {selectedStation && (
            <Col xs={24} sm={12} md={3}>
              <Popconfirm
                title="确认解绑"
                description={`解绑后"${selectedStation}"将从您的场站列表中消失，场站历史数据不受影响。如需重新绑定请联系管理员。`}
                onConfirm={() => unbindMut.mutate(selectedStation)}
                okText="确认解绑"
                cancelText="取消"
              >
                <Button
                  danger
                  icon={<DisconnectOutlined />}
                  loading={unbindMut.isPending}
                  style={{ width: '100%' }}
                >
                  解绑场站
                </Button>
              </Popconfirm>
            </Col>
          )}
        </Row>
      </div>

      {/* AGC 指标卡片 */}
      {agcMetrics && (
        <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Metric title="AGC 目标积分" value={fmtMWh(agcMetrics.targetMwh)} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Metric title="AGC 实际积分" value={fmtMWh(agcMetrics.actualMwh)} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Metric title="光伏实际积分" value={fmtMWh(agcMetrics.pvActualMwh)} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Metric title="日内总充电" value={fmtMWh(Math.abs(agcMetrics.charge))} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Metric title="日内总放电" value={fmtMWh(agcMetrics.discharge)} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Metric title="SOC 最终净留存" value={fmtMWh(agcMetrics.finalSoc)} />
          </Col>
        </Row>
      )}

      {/* AGC 图表 */}
      <div style={{ marginBottom: 24 }}>
        <ChartCard
          title="AGC 运行功率"
          option={agcChartOption}
          loading={isLoading}
          error={error ?? null}
          onRetry={() => refetch()}
          height={450}
        />
      </div>

      {/* FDKH 指标卡片 */}
      {fdkhMetrics && (
        <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Metric title="总计划电量" value={fmtMWh(fdkhMetrics.planMwh)} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Metric title="实际上网电量" value={fmtMWh(fdkhMetrics.actualMwh)} />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Metric
              title="总考核电量"
              value={fmtMWh(fdkhMetrics.assessMwh)}
              delta={`严重惩罚: ${fmtMWh(fdkhMetrics.sumHigh)}`}
              deltaColor="red"
            />
          </Col>
        </Row>
      )}

      {/* FDKH 图表 */}
      <div style={{ marginBottom: 24 }}>
        <ChartCard
          title="调度考核监控"
          option={fdkhChartOption}
          loading={isLoading}
          error={error ?? null}
          onRetry={() => refetch()}
          height={400}
        />
      </div>

      {/* Prediction 图表 */}
      <div style={{ marginBottom: 32 }}>
        <ChartCard
          title="功率预测精度"
          option={predictionChartOption}
          loading={isLoading}
          error={error ?? null}
          onRetry={() => refetch()}
          height={400}
        />
        {predictions.length === 0 && !isLoading && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Empty description="当日暂无预测时序数据，请确认上传的 Excel Sheet2 包含预测曲线。" />
          </div>
        )}
      </div>

      {/* Prediction Summary 指标卡片 */}
      {predictionSummary.length > 0 ? (
        predictionSummary.map((row, idx) => (
          <div key={idx} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              {row.power_type} 预测精度
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 0', minWidth: 160 }}>
                <Metric
                  title="上午日前精度 (7:20/7:30)"
                  value={`${row.dayahead_acc_0730 ?? '-'} %`}
                />
              </div>
              <div style={{ flex: '1 1 0', minWidth: 160 }}>
                <Metric
                  title="中午日前精度 (12:20/12:30)"
                  value={`${row.dayahead_acc_1220 ?? '-'} %`}
                />
              </div>
              <div style={{ flex: '1 1 0', minWidth: 160 }}>
                <Metric title="超短期精度" value={`${row.ultra_short_term_acc ?? '-'} %`} />
              </div>
              <div style={{ flex: '1 1 0', minWidth: 160 }}>
                <Metric title="日前上传率" value={`${row.dayahead_upload_rate ?? '-'} %`} />
              </div>
              <div style={{ flex: '1 1 0', minWidth: 160 }}>
                <Metric
                  title="超短期上传率"
                  value={`${row.ultra_short_term_upload_rate ?? '-'} %`}
                />
              </div>
            </div>
          </div>
        ))
      ) : (
        !isLoading && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Empty description="当日暂无静态预测精度指标。" />
          </div>
        )
      )}
    </PageContainer>
  );
};

export default PrivatePage;
